<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { entries } from 'lodash';
import { ColumnSchema, ColumnSchemaModel } from '@/modules/core/common/services/GridService';
import { useLocalization } from '@/plugins/localization';

defineOptions({
    name: 'ColumnsCustomizer',
});

const props = defineProps({
  "columns": null,
  "portalName": { default: 'view-manager-columns' },
  "title": { default: '[[[Kolumny]]]' },
  "refreshButton": { type: Boolean, default: false },
  "viewStateKey": { default: '' }
});

const emit = defineEmits(["definitionChanged", "refresh"]);

const { $t } = useLocalization();
const store = useStore();

const columnDef = ref<ColumnSchemaModel | null>(null);
const topOrBottom = ref('');
const draggedColumnKey = ref('');
const allCheckbox = ref(false);

const sortedColumns = computed<[string, ColumnSchema][]>(() =>
{
    if (!props.columns) return [];

    const sorted = entries(props.columns as Record<string, ColumnSchema>)
        .sort((a, b) =>
        {
            if (b[1].order < a[1].order) return 1;

            return -1;
        })
        .filter(
            (column) =>
                column[0] !== 'id' && column[0] !== 'publicId' && column[0] !== 'userId' && column[1].headerName
        );

    return sorted;
});

watch(
    sortedColumns,
    (sorted) =>
    {
        store.commit('common/grid/SET_COLUMNS_VISIBILITY', { key: props.viewStateKey, value: sorted.length > 0 });
        allCheckbox.value = sorted.some(([, value]) => value.visibility);
    },
    { immediate: true, deep: true }
);

const startDrag = (e: DragEvent, header: [string, ColumnSchema]): void =>
{
    const dt = e.dataTransfer;

    if (dt)
    {
        dt.dropEffect = 'move';
        dt.effectAllowed = 'move';
        dt.setData('columnKey', header[0]);
        dt.setData('orderId', String(header[1].order));
    }

    draggedColumnKey.value = header[0];
};

const checkInWhichSide = (e: DragEvent): void =>
{
    const target = e.target as HTMLDivElement;
    const link = target.closest('[data-column-key]') as HTMLElement | null;
    const linkColumnKey = link?.dataset.columnKey;

    if (linkColumnKey === draggedColumnKey.value || !link) return;

    const { height } = link.getBoundingClientRect();
    const halfHeight = height / 2;

    if (e.offsetY >= halfHeight)
    {
        link.classList.remove('top-bar');
        link.classList.add('bottom-bar');
        topOrBottom.value = 'bottom';
    }
    else
    {
        link.classList.remove('bottom-bar');
        link.classList.add('top-bar');
        topOrBottom.value = 'top';
    }
};

const clearBorders = (e: DragEvent): void =>
{
    const target = e.target as HTMLDivElement;
    const link = target.closest('[data-column-key]') as HTMLElement | null;

    link && link.classList.remove('top-bar', 'bottom-bar');
};

const onDrop = (e: DragEvent): void =>
{
    draggedColumnKey.value = '';
    document.querySelectorAll('[data-column-key]').forEach((elem) => elem.classList.remove('top-bar', 'bottom-bar'));

    const dt = e.dataTransfer;
    const dropTarget = e.target as HTMLElement;
    const link = dropTarget.closest('[data-column-key]') as HTMLElement | null;
    const targetOrderId = Number(link?.dataset.order);
    const draggedKey = dt?.getData('columnKey');
    const cols = props.columns || columnDef.value;

    if (!cols || !link || !draggedKey) return;

    const item = entries(cols as Record<string, ColumnSchema>).find((column) => column[0] === draggedKey);

    if (!item) return;

    if (item[1].order === targetOrderId) return;

    const sorted = entries(cols as Record<string, ColumnSchema>)
        .filter((c) => c[0] !== 'id' && c[0] !== 'publicId' && c[0] !== 'userId' && c[1].headerName)
        .sort((a, b) => (a[1].order > b[1].order ? 1 : -1));

    const draggedIndex = sorted.findIndex((c) => c[0] === draggedKey);
    const targetIndex = sorted.findIndex((c) => c[1].order === targetOrderId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    const insertIndex = topOrBottom.value === 'top' ? targetIndex : targetIndex + 1;
    const [dragged] = sorted.splice(draggedIndex, 1);
    const newIndex = insertIndex > draggedIndex ? insertIndex - 1 : insertIndex;

    sorted.splice(newIndex, 0, dragged);

    sorted.forEach((entry, i) =>
    {
        entry[1].order = i + 1;
    });

    emit('definitionChanged', cols);
};

const canSwitchUp = (item: [string, ColumnSchema]): boolean => item[1].order !== 1;

const switchUp = (item: [string, ColumnSchema]): void =>
{
    const cols = columnDef.value;

    if (!cols) return;

    const columnsEntries = entries(cols as Record<string, ColumnSchema>);
    const column = columnsEntries
        .sort((a, b) => (b[1].order < a[1].order ? 1 : -1))
        .filter((c) => c[0] !== 'id')
        .find((c) => item[1].order - c[1].order === 1);

    if (column)
    {
        column[1].order++;
        item[1].order--;
        emit('definitionChanged', cols);
    }
};

const canSwitchDown = (item: [string, ColumnSchema]): boolean =>
{
    const sorted = entries(columnDef.value as Record<string, ColumnSchema>)
        .sort((a, b) => (b[1].order < a[1].order ? 1 : -1))
        .filter((c) => c[0] !== 'id');

    return item[1].order !== sorted.length;
};

const switchDown = (item: [string, ColumnSchema]): void =>
{
    const cols = columnDef.value;

    if (!cols) return;

    const columnsEntries = entries(cols as Record<string, ColumnSchema>);
    const column = columnsEntries
        .sort((a, b) => (b[1].order < a[1].order ? 1 : -1))
        .filter((c) => c[0] !== 'id')
        .find((c) => item[1].order - c[1].order === -1);

    if (column)
    {
        column[1].order--;
        item[1].order++;
        emit('definitionChanged', cols);
    }
};

const columnVisibilityChanged = (value: boolean, item: ColumnSchema): void =>
{
    item.visibility = value;

    const cols = columnDef.value;

    if (cols) emit('definitionChanged', cols);
};

const changeAllCheckbox = (value: boolean): void =>
{
    allCheckbox.value = value;

    const cols = columnDef.value;

    if (cols)
    {
        entries(cols as Record<string, ColumnSchema>).forEach((column) =>
        {
            column[1].visibility = allCheckbox.value;
        });
        emit('definitionChanged', cols);
    }
};

const refreshColumns = (): void => emit('refresh');

watch(
    () => props.columns,
    (columns) =>
    {
        columnDef.value = columns;
    },
    { immediate: true }
);
</script>

<template>
    <Portal :to="portalName">
        <DataCard overflow scroll class="overflow-hidden flex-shrink-1">
            <template #header>
                <div class="d-flex justify-content-between">
                    <IdeoFormCheckbox :model-value="allCheckbox" @change="changeAllCheckbox">
                        <strong>{{ $t(title) }}</strong>
                    </IdeoFormCheckbox>
                    <IdeoTooltip v-if="refreshButton" :tooltip="$t('[[[Przywróć domyślną listę kolumn]]]')" position="left">
                        <IdeoButton variant="primary" size="sm" icon="fas fa-refresh" @click="refreshColumns" />
                    </IdeoTooltip>
                </div>
            </template>
            <template #default>
                <div v-if="mobile" class="columns-customizer scroll scroll--overflow-x-hidden">
                    <div
                        v-for="header in sortedColumns"
                        :key="header[0]"
                        :data-column-key="header[0]"
                        :data-order="header[1].order"
                        data-unclosing="true"
                        class="dropdown-item px-3"
                    >
                        <div class="d-flex align-items-center">
                            <div class="d-flex flex-column me-2">
                                <button
                                    :disabled="!canSwitchUp(header)"
                                    class="border-0 bg-transparent mb-1 order-switcher"
                                    type="button"
                                    @click="switchUp(header)"
                                >
                                    <span class="fas fa-arrow-alt-up"></span>
                                </button>
                                <button
                                    :disabled="!canSwitchDown(header)"
                                    class="border-0 bg-transparent order-switcher"
                                    type="button"
                                    @click="switchDown(header)"
                                >
                                    <span class="fas fa-arrow-alt-down"></span>
                                </button>
                            </div>
                            <IdeoFormCheckbox
                                :model-value="header[1].visibility"
                                @change="columnVisibilityChanged($event, header[1])"
                            >
                                {{ header[1].headerName }}
                            </IdeoFormCheckbox>
                        </div>
                    </div>
                </div>
                <div v-else class="columns-customizer flex-shrink-1 scroll scroll--overflow-x-hidden">
                    <div
                        class="drop-zone"
                        @drop="onDrop($event)"
                        @dragover.prevent="checkInWhichSide"
                        @dragenter.prevent
                        @dragleave.prevent="clearBorders"
                    >
                        <div
                            v-for="header in sortedColumns"
                            :key="header[0]"
                            draggable="true"
                            :data-column-key="header[0]"
                            :data-order="header[1].order"
                            style="cursor: move"
                            class="dropdown-item py-2 px-3"
                            data-unclosing="true"
                            @dragstart="startDrag($event, header)"
                        >
                            <div class="d-flex align-items-center">
                                <span class="fas fa-grip-vertical me-2"></span>
                                <IdeoFormCheckbox
                                    :model-value="header[1].visibility"
                                    @change="columnVisibilityChanged($event, header[1])"
                                >
                                    {{ header[1].headerName }}
                                </IdeoFormCheckbox>
                            </div>
                        </div>
                        <template v-if="!sortedColumns.length">
                            <p class="mb-0 p-2 px-3">{{ $t('[[[Brak kolumn]]]') }}</p>
                        </template>
                    </div>
                </div>
            </template>
        </DataCard>
    </Portal>
</template>

<style lang="scss" scoped>
.order-switcher {
    color: var(--filter-nav-link-color);
}

button[disabled] {
    opacity: 0.6;
}

.top-bar {
    box-shadow: 0 -3px 0 $danger;
    position: relative;
    z-index: 2;
}

.bottom-bar {
    box-shadow: 0 3px 0 0 $danger;
    position: relative;
    z-index: 2;
}

.columns-customizer {
    margin: -10px;

    .card-header {
        padding: 0.5rem 1rem;
    }

    .dropdown-item {
        &:last-of-type {
            border-bottom: 0;
        }
    }
}
</style>
